// Cài đặt › Nhân viên (chỉ chủ đơn vị — docs/KE_HOACH_THANH_VIEN_DON_VI.md): người trong đơn vị, vai trò (tên tự đặt +
// quyền), nhân viên đã làm gì. Nhân viên chỉ làm được việc của vai trò, chỉ trong đơn vị này.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, ShieldCheck, UserRound } from 'lucide-react'
import { del, get, patch, post } from '@/lib/api'
import { formatPhoneVN } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { IndexBadge } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { confirm } from '@/components/ui/confirm'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { UNIT_PERMISSION_META, isUnitPermission } from '@shoptrack/common/unit-permissions'
import { AddMemberDialog } from './AddMemberDialog'
import { CredentialsBox } from './CredentialsBox'
import { RoleDialog } from './RoleDialog'
import { activityOrderLink, browserRandom, makeTempPassword, staffActionLabel, type ActivityItem, type MemberRow, type MembersResponse, type UnitRoleRow } from './types'

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('vi-VN') : '—')
const fmtTime = (d: string) => new Date(d).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })

function permissionSummary(perms: string[]): string {
  const labels = perms.filter(isUnitPermission).map((p) => UNIT_PERMISSION_META[p].label)
  return labels.length ? labels.join(' · ') : 'Chưa có quyền nào'
}

export function MembersTab() {
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({ queryKey: ['unit-members'], queryFn: () => get<MembersResponse>('/settings/members') })
  const [adding, setAdding] = useState(false)
  const [editingRole, setEditingRole] = useState<UnitRoleRow | 'new' | null>(null)
  const [changingRole, setChangingRole] = useState<MemberRow | null>(null)
  const [resetting, setResetting] = useState<MemberRow | null>(null)

  const refresh = () => qc.invalidateQueries({ queryKey: ['unit-members'] })
  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { void refresh() },
    onError: (e) => toast.error((e as Error).message),
  })

  if (isLoading) return <div className="flex items-center gap-2 text-sm text-muted"><Spinner /> Đang tải…</div>
  if (error || !data) return <p className="text-sm text-danger">{(error as Error)?.message || 'Không tải được danh sách nhân viên'}</p>

  const staff = data.members.filter((m) => !m.isOwner)
  const owners = data.members.filter((m) => m.isOwner)

  async function onSuspend(m: MemberRow) {
    const suspend = m.status !== 'suspended'
    const ok = await confirm({
      title: suspend ? `Tạm khóa ${m.name || 'nhân viên'}?` : `Mở khóa ${m.name || 'nhân viên'}?`,
      description: suspend ? 'Người này không vào được đơn vị cho tới khi bạn mở khóa.' : undefined,
      confirmLabel: suspend ? 'Tạm khóa' : 'Mở khóa',
      tone: suspend ? 'danger' : 'default',
    })
    if (ok) act.mutate(() => patch(`/settings/members/${m.userId}`, { suspended: suspend }).then(() => toast.success(suspend ? 'Đã tạm khóa' : 'Đã mở khóa')))
  }

  async function onRemove(m: MemberRow) {
    const ok = await confirm({
      title: `Gỡ ${m.name || 'nhân viên'} khỏi đơn vị?`,
      description: 'Người này mất quyền vào đơn vị ngay.',
      confirmLabel: 'Gỡ khỏi đơn vị',
      tone: 'danger',
    })
    if (ok) act.mutate(() => del(`/settings/members/${m.userId}`).then(() => toast.success('Đã gỡ nhân viên')))
  }

  async function onDeleteRole(r: UnitRoleRow) {
    const ok = await confirm({ title: `Xóa vai trò "${r.name}"?`, confirmLabel: 'Xóa vai trò', tone: 'danger' })
    if (ok) act.mutate(() => del(`/settings/members/roles/${r.id}`).then(() => toast.success('Đã xóa vai trò')))
  }

  return (
    <div className="space-y-6">
      {/* ── Nhân viên (chủ + nhân viên) ── */}
      <section className="rounded-xl bg-raise shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-base text-ink">Nhân viên</h3>
            <p className="text-xs text-muted">{staff.length}/{data.limits.maxStaff} nhân viên</p>
          </div>
          <Button
            type="button"
            size="sm"
            className="h-11 desktop:h-9"
            disabled={!data.roles.length || staff.length >= data.limits.maxStaff}
            title={!data.roles.length ? 'Tạo vai trò trước' : undefined}
            onClick={() => setAdding(true)}
          >
            <Plus className="mr-1 size-4" /> Thêm nhân viên
          </Button>
        </div>
        <ul className="divide-y divide-rule">
          {owners.map((m, i) => (
            <li key={m.userId} className="flex items-center gap-3 px-4 py-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent/10 text-accent"><ShieldCheck className="size-4" aria-hidden /></span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-medium text-ink">
                  <IndexBadge n={i + 1} />
                  <span className="min-w-0 flex-1 truncate">{m.name || formatPhoneVN(m.phone ?? '') || 'Chủ đơn vị'}{m.isMe ? ' (bạn)' : ''}</span>
                </p>
                <p className="truncate text-xs text-muted">{[m.phone && formatPhoneVN(m.phone), m.email].filter(Boolean).join(' · ')}</p>
              </div>
              <span className="shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-xs font-medium text-accent">Chủ đơn vị</span>
            </li>
          ))}
          {staff.map((m, i) => (
            <li key={m.userId} className="space-y-2 px-4 py-3">
              <div className="flex items-start gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-paperSoft text-inkSoft"><UserRound className="size-4" aria-hidden /></span>
                <div className="min-w-0 flex-1">
                  {/* STT nối tiếp sau chủ đơn vị — cùng một danh sách */}
                  <p className="flex items-center gap-2 text-sm font-medium text-ink">
                    <IndexBadge n={owners.length + i + 1} />
                    <span className="min-w-0 flex-1 truncate">{m.name || formatPhoneVN(m.phone ?? '')}</span>
                  </p>
                  <p className="truncate text-xs text-muted">{[m.phone && formatPhoneVN(m.phone), m.email].filter(Boolean).join(' · ')}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    Thêm {fmtDate(m.addedAt)} · Đăng nhập gần nhất {fmtDate(m.lastLoginAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="rounded-full bg-paperSoft px-2 py-0.5 text-xs font-medium text-ink">{m.roleName ?? 'Chưa có vai trò'}</span>
                  {m.status === 'suspended' && <span className="text-xs font-medium text-warn">Đang tạm khóa</span>}
                  {m.mustChangePassword && m.status !== 'suspended' && <span className="text-xs text-muted">Chưa đổi mật khẩu tạm</span>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pl-12">
                <Button type="button" size="sm" variant="outline" className="h-10 desktop:h-8" onClick={() => setChangingRole(m)} disabled={act.isPending}>Đổi vai trò</Button>
                <Button type="button" size="sm" variant="outline" className="h-10 desktop:h-8" onClick={() => onSuspend(m)} disabled={act.isPending}>
                  {m.status === 'suspended' ? 'Mở khóa' : 'Tạm khóa'}
                </Button>
                {m.canResetPassword && (
                  <Button type="button" size="sm" variant="outline" className="h-10 desktop:h-8" onClick={() => setResetting(m)} disabled={act.isPending}>Đặt lại mật khẩu</Button>
                )}
                <Button type="button" size="sm" variant="ghost" className="h-10 text-danger hover:text-danger desktop:h-8" onClick={() => onRemove(m)} disabled={act.isPending}>Gỡ khỏi đơn vị</Button>
              </div>
            </li>
          ))}
          {!staff.length && <li className="px-4 py-6 text-center text-sm text-muted">Chưa có nhân viên nào.</li>}
        </ul>
      </section>

      {/* ── Vai trò ── */}
      <section className="rounded-xl bg-raise shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-base text-ink">Vai trò</h3>
          </div>
          <Button type="button" size="sm" variant="outline" className="h-11 desktop:h-9" disabled={data.roles.length >= data.limits.maxRoles} onClick={() => setEditingRole('new')}>
            <Plus className="mr-1 size-4" /> Tạo vai trò
          </Button>
        </div>
        <ul className="divide-y divide-rule">
          {data.roles.map((r, i) => (
            <li key={r.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="flex items-start gap-2 text-sm font-medium text-ink">
                  <IndexBadge n={i + 1} />
                  <span className="min-w-0 flex-1">{r.name} <span className="font-normal text-muted">· {r.memberCount} người</span></span>
                </p>
                <p className="mt-0.5 text-xs text-inkSoft">{permissionSummary(r.permissions)}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button type="button" size="sm" variant="outline" className="h-10 desktop:h-8" onClick={() => setEditingRole(r)} disabled={act.isPending}>Sửa</Button>
                <Button
                  type="button" size="sm" variant="ghost" className="h-10 text-danger hover:text-danger desktop:h-8"
                  disabled={act.isPending || r.memberCount > 0}
                  title={r.memberCount > 0 ? 'Đang có người dùng' : undefined}
                  onClick={() => onDeleteRole(r)}
                >
                  Xóa
                </Button>
              </div>
            </li>
          ))}
          {!data.roles.length && <li className="px-4 py-6 text-center text-sm text-muted">Chưa có vai trò nào.</li>}
        </ul>
      </section>

      <ActivitySection staff={staff} />

      <AddMemberDialog open={adding} onOpenChange={setAdding} roles={data.roles} passwordMin={data.limits.passwordMin} />
      <RoleDialog
        open={editingRole !== null}
        onOpenChange={(v) => { if (!v) setEditingRole(null) }}
        role={editingRole === 'new' ? null : editingRole}
        tenantType={data.unit.tenantType}
        grantable={data.grantable}
      />
      <ChangeRoleDialog member={changingRole} roles={data.roles} onClose={() => setChangingRole(null)} />
      <ResetPasswordDialog member={resetting} passwordMin={data.limits.passwordMin} onClose={() => setResetting(null)} />
    </div>
  )
}

function ChangeRoleDialog({ member, roles, onClose }: { member: MemberRow | null; roles: UnitRoleRow[]; onClose: () => void }) {
  const qc = useQueryClient()
  const [roleId, setRoleId] = useState('')
  const open = member !== null
  useEffect(() => { setRoleId(member?.roleId ?? '') }, [member?.userId, member?.roleId])
  const save = useMutation({
    mutationFn: () => patch(`/settings/members/${member!.userId}`, { roleId }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['unit-members'] }); toast.success('Đã đổi vai trò'); onClose() },
    onError: (e) => toast.error((e as Error).message),
  })
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !save.isPending) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Đổi vai trò</DialogTitle>
          <DialogDescription>{member?.name}</DialogDescription>
        </DialogHeader>
        <Select value={roleId} onValueChange={setRoleId} disabled={save.isPending}>
          <SelectTrigger aria-label="Vai trò"><SelectValue placeholder="Chọn vai trò" /></SelectTrigger>
          <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>Hủy</Button>
          <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || !roleId || roleId === member?.roleId}>{save.isPending ? 'Đang lưu…' : 'Lưu'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ResetPasswordDialog({ member, passwordMin, onClose }: { member: MemberRow | null; passwordMin: number; onClose: () => void }) {
  const qc = useQueryClient()
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)
  const open = member !== null
  // Mở cho người khác → mật khẩu tạm mới; KHÔNG đặt lại khi danh sách tải lại (đang hiện mật khẩu vừa đặt)
  useEffect(() => {
    if (!member) return
    setPassword(makeTempPassword(browserRandom))
    setDone(false)
  }, [member?.userId])
  const save = useMutation({
    mutationFn: () => post(`/settings/members/${member!.userId}/password`, { password }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['unit-members'] }); setDone(true) },
    onError: (e) => toast.error((e as Error).message),
  })
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !save.isPending) onClose() }}>
      <DialogContent className="sm:max-w-md" aria-describedby={done ? undefined : 'reset-pw-desc'}>
        <DialogHeader>
          <DialogTitle>{done ? 'Đã đặt lại mật khẩu' : 'Đặt lại mật khẩu'}</DialogTitle>
          {!done && (
            <DialogDescription id="reset-pw-desc">
              {`${member?.name ?? 'Nhân viên'} sẽ bị đăng xuất khỏi mọi thiết bị.`}
            </DialogDescription>
          )}
        </DialogHeader>
        {done && member ? (
          <CredentialsBox name={member.name ?? ''} phone={member.phone ?? ''} password={password} />
        ) : (
          <div className="flex gap-2">
            <Input value={password} onChange={(e) => setPassword(e.target.value)} maxLength={100} className="h-11 font-mono desktop:h-9" aria-label="Mật khẩu tạm mới" autoComplete="off" />
            <Button type="button" variant="outline" className="h-11 shrink-0 desktop:h-9" onClick={() => setPassword(makeTempPassword(browserRandom))}>Tạo mới</Button>
          </div>
        )}
        <DialogFooter>
          {done ? (
            <Button type="button" onClick={onClose}>Đóng</Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>Hủy</Button>
              <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || password.length < passwordMin}>{save.isPending ? 'Đang lưu…' : 'Đặt lại'}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ActivitySection({ staff }: { staff: MemberRow[] }) {
  const [userId, setUserId] = useState<string>('all')
  const q = useInfiniteQuery({
    queryKey: ['unit-members-activity', userId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: '30' })
      if (userId !== 'all') params.set('userId', userId)
      if (pageParam) params.set('before', pageParam)
      return get<{ items: ActivityItem[]; next: string | null }>(`/settings/members/activity?${params}`)
    },
    getNextPageParam: (last) => last.next,
    enabled: staff.length > 0,
  })
  const items = q.data?.pages.flatMap((p) => p.items) ?? []
  if (!staff.length) return null
  return (
    <section className="rounded-xl bg-raise shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-3">
        <h3 className="text-base text-ink">Nhân viên đã làm gì</h3>
        <div className="w-full sm:w-56">
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger aria-label="Lọc theo nhân viên"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Mọi nhân viên</SelectItem>
              {staff.map((m) => <SelectItem key={m.userId} value={m.userId}>{m.name || formatPhoneVN(m.phone ?? '')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {q.isLoading ? (
        <p className="flex items-center gap-2 px-4 py-4 text-sm text-muted"><Spinner /> Đang tải…</p>
      ) : !items.length ? (
        <p className="px-4 py-6 text-center text-sm text-muted">Chưa có thao tác nào.</p>
      ) : (
        <ul className="divide-y divide-rule">
          {items.map((it, i) => {
            const link = activityOrderLink(it.action, it.target)
            return (
              <li key={it.id} className="flex items-start justify-between gap-3 px-4 py-2.5 text-sm">
                <div className="flex min-w-0 items-start gap-2">
                  <IndexBadge n={i + 1} />
                  <p className="min-w-0 text-ink">
                    <span className="font-medium">{it.name ?? 'Nhân viên'}</span> · {staffActionLabel(it.action)}
                    {it.target && (link ? <> · <Link to={link} className="font-mono text-accent hover:underline">{it.target}</Link></> : <span className="font-mono text-muted"> · {it.target}</span>)}
                  </p>
                </div>
                <span className="shrink-0 text-xs text-muted">{fmtTime(it.at)}</span>
              </li>
            )
          })}
        </ul>
      )}
      {q.hasNextPage && (
        <div className="border-t border-rule p-3 text-center">
          <Button type="button" size="sm" variant="outline" className="h-10 desktop:h-8" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
            {q.isFetchingNextPage ? 'Đang tải…' : 'Xem thêm'}
          </Button>
        </div>
      )}
    </section>
  )
}
